import type { ExtractFnReturnType } from "apps/visits/core/lib/react-query";
import type { AxiosError } from "axios";
import { chain } from "lodash";
import { useRef } from "react";
import { type UseQueryOptions, useQuery } from "react-query";
import type { ApiOrder, Order, VouchersOrderStatus } from "../models/order";
import type { OrderItem } from "../models/order-item";
import { VouchersDistributionsRepository } from "./vouchers-distribution.api";
import type { GetOrderDTO } from "./vouchers-distribution.types";

const vouchersDistributionsRepository = new VouchersDistributionsRepository();

// REV-125: PayU `continue_url` can beat the webhook, so a `created` order is re-checked briefly.
export const ORDER_POLL_INTERVAL_MS = 3000;
export const ORDER_POLL_BUDGET_MS = 60_000;

export const orderRefetchInterval = (status: VouchersOrderStatus | undefined, elapsedMs: number): number | false =>
  status === "created" && elapsedMs < ORDER_POLL_BUDGET_MS ? ORDER_POLL_INTERVAL_MS : false;

type QueryFnType = typeof vouchersDistributionsRepository.getOrder;
type ApiData = ExtractFnReturnType<QueryFnType>;
type SelectReturn = { order: Order };
type UseGetOrderOptions = {
  config?: UseQueryOptions<ApiData, AxiosError, SelectReturn>;
} & GetOrderDTO;

export const useGetOrder = ({ config, ...rest }: UseGetOrderOptions) => {
  // The poll budget starts at the first response, not at mount: the query can stay disabled until the
  // distribution loads, and the first request can spend time in `retry` backoff.
  const startedAtRef = useRef<number>();

  return useQuery({
    queryKey: ["order", rest.slug, rest.configName, rest.uuid],
    queryFn: () => vouchersDistributionsRepository.getOrder(rest),
    select: (data) => ({ order: mapApiOrderToOrder(data.order) }),
    refetchOnWindowFocus: false,
    refetchInterval: (data) => {
      if (data !== undefined && startedAtRef.current === undefined) startedAtRef.current = Date.now();
      const elapsedMs = startedAtRef.current === undefined ? 0 : Date.now() - startedAtRef.current;
      return orderRefetchInterval(data?.order.status, elapsedMs);
    },
    // A failed poll must not replace an already rendered Thank You Page with the error boundary.
    useErrorBoundary: (_error, query) => query.state.data === undefined,
    retry: 2,
    ...config,
  });
};

export const mapApiOrderToOrder = (apiOrder: ApiOrder): Order => {
  const items: OrderItem[] = chain(apiOrder.items)
    .groupBy("productUuid")
    .map((group) => ({
      ...group[0],
      quantity: group.length,
    }))
    .value();

  return {
    ...apiOrder,
    items,
  };
};
